import type {
  UploadFileProps,
  UploadOnChange,
  UseUploadFileTextsProps,
} from "./upload-file.type";
import type { UploadFile, UploadProps } from "antd";

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppNotification } from "@/modules/app-notification";
import { downloadFileFromBlob } from "@/modules/file-downloader";
import { usePreviewModal } from "@/modules/preview-modal";
import { convertFileToBase64 } from "@/utilities/file";

import { useUploadFileValidator } from "./upload-file-validator";
import { UPLOAD_MAX_FILE_SIZE } from "./upload-file.constant";
import { getUploadAuthorizedFileTypes } from "./upload-file.helper";

export const useUploadFile = ({
  type = "file",
  fileTypes,
  multiple,
  maxCount,
  maxFileSize = UPLOAD_MAX_FILE_SIZE,
  layout = "draggerColumn",
  translationType,
  onChange,
  onPreview,
  onBeforeUpload,
  ...customUploadProps
}: UploadFileProps) => {
  const { onPreviewFile, onPreviewImage } = useUploadFilePreview();

  const defaultList =
    customUploadProps.fileList ?? customUploadProps.defaultFileList;
  const [fileList, setFileList] = useState<UploadFile[]>(defaultList ?? []);

  const authorizedFileTypes = getUploadAuthorizedFileTypes({
    type,
    fileTypes,
  });

  const texts = useUploadFileTexts({
    type,
    multiple,
    maxCount,
    maxFileSize,
    translationType,
    fileListCount: fileList.length,
  });

  const handleChange: UploadOnChange<UploadFile> = (info) => {
    onChange?.(info);
    setFileList(info.fileList);
  };

  const handlePreview = (file: UploadFile) => {
    onPreview?.(file);
    type === "image" ? onPreviewImage(file) : onPreviewFile(file);
  };

  const { beforeUpload } = useUploadFileValidator({
    authorizedFileTypes,
    maxFileSize,
    onBeforeUpload,
  });

  const uploadProps: UploadProps = {
    customRequest: ({ onSuccess }) => onSuccess?.(""), // disable default xhr behavior (our SDK will process the file)
    listType: type === "image" ? "picture-card" : undefined,
    ...customUploadProps,
    accept: authorizedFileTypes.join(","),
    multiple,
    maxCount,
    onChange: handleChange,
    onPreview: handlePreview,
    beforeUpload,
  };

  return {
    uploadProps,
    layout,
    authorizedFileTypes,
    texts,
  };
};

export const useUploadFilePreview = () => {
  const { t } = useTranslation();
  const { notify } = useAppNotification();
  const { openPreviewModal } = usePreviewModal();

  const onPreviewFile = (file: UploadFile) => {
    if (!file.url) return;

    fetch(file.url)
      .then((resp) => resp.blob())
      .then((blob) => downloadFileFromBlob(blob, file.name))
      .catch(() => notify.error({ title: t("global:downloadFailed") }));
  };

  const onPreviewImage = async (file: UploadFile) => {
    let imageSrc: unknown = file.url;

    if (!imageSrc && file.originFileObj) {
      imageSrc = await convertFileToBase64(file.originFileObj);
    }

    if (typeof imageSrc !== "string") return;

    openPreviewModal({ imageSrc });
  };

  return {
    onPreviewFile,
    onPreviewImage,
  };
};

export const useUploadFileTexts = ({
  type,
  multiple,
  maxCount,
  maxFileSize,
  translationType,
  fileListCount,
}: UseUploadFileTextsProps) => {
  const { t } = useTranslation();
  const transType = translationType ?? type;

  // handle singular/plural translations
  const count = multiple ? 2 : 1;

  const showTitleMaxCount = maxCount && maxCount > 1;
  const showTitleReplace = (!multiple || maxCount === 1) && fileListCount === 1;

  const title = showTitleMaxCount
    ? t(`global:upload.titleMaxCount.${transType}`, { maxCount })
    : showTitleReplace
      ? t(`global:upload.titleReplace.${transType}`, { count })
      : t(`global:upload.title.${transType}`, { count });

  const maxSize =
    type !== "image"
      ? t("global:upload.maxFileSize", { maxFileSize, count })
      : undefined;

  return {
    title,
    maxSize,
  };
};
